import React, { FC } from 'react';
import { ButtonV2, CloseButton, Flex, styled, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { buttonSharedStates } from '@shared/utils/style/buttons';
import { ResponsiveDialog } from '../ResponsiveDialog';
import { closeButtonOverrides, drawerOverrides } from '../ResponsiveDialog/ResponsiveDialog.styles';

type CancelConfirmationDialogProps = {
  isOpen: boolean;
  handleOnDiscardChangesClick: () => void;
  handleOnCloseClick: () => void;
  handleOnSaveClick: () => void;
  isSaveDisabled: boolean;
  isUpdating: boolean;
};

const StyledActionButton = styled(ButtonV2)`
  ${buttonSharedStates};
`;

export const CancelConfirmationDialog: FC<CancelConfirmationDialogProps> = props => {
  const { isOpen, handleOnDiscardChangesClick, handleOnCloseClick, handleOnSaveClick, isSaveDisabled, isUpdating } = props;

  const { t } = useTranslation('joykit');
  const translationFn = t('focusPointDialog', 'cancelConfirmationDialog');

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={handleOnCloseClick}
      hideDivider={true}
      removeBodyPadding
      hasCloseButton={false}
      disableCloseOnOutsideClick={true}
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)'
          }
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingY: 8, position: 'relative' } }
      }}
      dialogOverrides={{
        Content: { props: { width: ['100%', '480px'] } },
        Body: { props: { paddingX: 8, paddingY: 8 } }
      }}
      disableFocusLock={true}
    >
      <Flex flexDirection="column" width="100%" height="100%" paddingX={{ _: 0, xs: 6 }} paddingY={7}>
        <Flex flexDirection="row" justifyContent="space-between" alignItems="center" gap={6}>
          <TextV2 as="h3" typographyVariant="hed6" fontWeight={600} textAlign="center" width="100%">
            {translationFn.title()}
          </TextV2>
          <CloseButton
            overrides={{
              Root: { props: { ...closeButtonOverrides.Root?.props, position: 'absolute', right: 20, top: 20 } },
              Icon: { props: { ...closeButtonOverrides.Icon?.props } }
            }}
            onClick={handleOnCloseClick}
            aria-label={'close dialog'}
          />
        </Flex>
        <TextV2 typographyVariant="body2" paddingTop={5}>
          {translationFn.content()}
        </TextV2>
        <Flex flexDirection="column" width="100%" gap={5} marginTop={7}>
          <StyledActionButton intent="neutral" size="lg" shape="rounded" variant="solid" onClick={handleOnSaveClick} disabled={isUpdating || isSaveDisabled} loading={isUpdating}>
            {translationFn.save()}
          </StyledActionButton>
          <StyledActionButton color="mono14" intent="neutral" size="md" variant="ghost" shape="rounded" onClick={handleOnDiscardChangesClick} disabled={isUpdating}>
            {translationFn.discardChanges()}
          </StyledActionButton>
        </Flex>
      </Flex>
    </ResponsiveDialog>
  );
};
